import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { Logo } from '@/components/layout/logo';
import { CodeBlock } from '@/components/ui/dev-note';
import { Alert, Card, CardBody, Container, Spinner } from '@/components/ui/primitives';
import { appConfig, configErrors, maskKey  } from '@/config/env';
import type {ConfigError} from '@/config/env';
import type { ErrorPresentation } from '@/lib/errors';

/**
 * Écrans de démarrage.
 *
 * Le site ne monte aucune page tant que `GET /site` n'a pas répondu : sans
 * cette réponse, on ignore le nom du site, son pays, son environnement et son
 * offre. Plutôt que de laisser chaque écran gérer l'absence de configuration,
 * on la traite une fois pour toutes ici.
 *
 * Cela produit trois états possibles au chargement :
 *
 * 1. **configuration invalide** — une variable d'environnement manque ou est
 *    malformée. On le dit franchement, avec la correction à apporter ;
 * 2. **chargement** — l'appel est en cours ;
 * 3. **échec de l'appel** — clé refusée, API injoignable, domaine non
 *    autorisé. On affiche le diagnostic et un bouton pour réessayer.
 *
 * Ces écrans sont volontairement explicites. Un site en production
 * remplacerait le troisième par un message plus sobre — mais surtout pas par
 * un écran blanc : une erreur de configuration silencieuse est la pire des
 * situations.
 */

function BootShell({ children }: { children: ReactNode }) {
    return (
        <div className="flex min-h-screen flex-col items-center justify-center">
            <Container className="max-w-2xl space-y-8">
                <div className="flex justify-center">
                    <Logo />
                </div>
                {children}
            </Container>
        </div>
    );
}

/** État 1 : les variables d'environnement ne sont pas exploitables. */
export function ConfigurationErrorScreen({ errors = configErrors }: { errors?: ConfigError[] }) {
    return (
        <BootShell>
            <Card className="border-critical/30">
                <CardBody className="space-y-6">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="text-critical mt-0.5 size-5 shrink-0" aria-hidden />
                        <div>
                            <h1 className="text-ink font-sans text-lg font-semibold">
                                Configuration du front incomplète
                            </h1>
                            <p className="text-ink-soft mt-1 text-sm">
                                Ce site ne peut pas démarrer tant que ces variables d'environnement ne sont pas
                                correctement renseignées.
                            </p>
                        </div>
                    </div>

                    <ul className="space-y-4">
                        {errors.map((error) => (
                            <li key={error.variable} className="border-line rounded-lg border p-4">
                                <code className="text-brand-700 font-mono text-xs">{error.variable}</code>
                                <p className="text-ink mt-1 text-sm font-medium">{error.problem}</p>
                                <p className="text-ink-soft mt-1 text-sm">{error.fix}</p>
                            </li>
                        ))}
                    </ul>

                    <div>
                        <p className="text-ink-soft mb-2 text-sm">Créez un fichier `.env` à la racine du projet :</p>
                        <CodeBlock label=".env">
                            {`VITE_DASHLITE_SITE_KEY=site_test_votre_cle
VITE_DASHLITE_API_URL=https://dash-lite.com
VITE_SHOW_API_INSPECTOR=true`}
                        </CodeBlock>
                        <p className="text-ink-muted mt-2 text-xs">
                            Vite ne lit les variables d'environnement qu'au démarrage : relancez `npm run dev` après
                            modification.
                        </p>
                    </div>
                </CardBody>
            </Card>
        </BootShell>
    );
}

/** État 2 : `GET /site` est en cours. */
export function BootLoadingScreen() {
    return (
        <BootShell>
            <div className="flex justify-center">
                <Spinner label="Chargement de la configuration du site…" />
            </div>
        </BootShell>
    );
}

/** État 3 : `GET /site` a échoué. */
export function BootErrorScreen({ error, onRetry }: { error: ErrorPresentation; onRetry: () => void }) {
    return (
        <BootShell>
            <Alert
                tone="critical"
                title={error.message}
                action={
                    <Button variant="secondary" size="sm" onClick={onRetry}>
                        <RefreshCw className="size-4" aria-hidden />
                        Réessayer
                    </Button>
                }
            >
                <p>{error.hint}</p>

                {error.configuration && (
                    <>
                        <p className="mt-3">Ce que ce front a essayé de joindre :</p>

                        <dl className="mt-2 space-y-1 font-mono text-xs">
                            <div>
                                <dt className="inline opacity-70">appel : </dt>
                                <dd className="inline">GET {appConfig.apiUrl}/api/v1/site</dd>
                            </div>
                            <div>
                                <dt className="inline opacity-70">X-Site-Key : </dt>
                                <dd className="inline">{maskKey(appConfig.siteKey)}</dd>
                            </div>
                            <div>
                                <dt className="inline opacity-70">origine : </dt>
                                <dd className="inline">{window.location.origin}</dd>
                            </div>
                        </dl>

                        <p className="mt-3">Trois causes, par ordre de fréquence :</p>

                        <ol className="mt-1 list-decimal space-y-1 pl-5">
                            <li>
                                l'adresse ci-dessus ne répond pas — ouvrez-la dans un onglet pour vérifier, et
                                corrigez <code className="font-mono">VITE_DASHLITE_API_URL</code> ;
                            </li>
                            <li>
                                l'origine ci-dessus n'est pas déclarée dans les domaines autorisés du site, pour cet
                                environnement (CORS). En test, seuls{' '}
                                <code className="font-mono">localhost</code> et{' '}
                                <code className="font-mono">127.0.0.1</code> sont autorisés d'office : tout autre
                                domaine doit être déclaré ;
                            </li>
                            <li>la clé de site ne correspond pas à ce site.</li>
                        </ol>
                    </>
                )}
            </Alert>
        </BootShell>
    );
}
